import React from "react"
import { APIResponse } from "redux/apis/types"
import { get, find, map, reduce, isNaN } from "lodash"
import * as Formatter from "utils/Formatter"
import ReactResizeDetector from "react-resize-detector"

import { chartColor, PieChart } from "components/atoms/chart/pieChart"

type Props = {
  statisticsData?: Array<APIResponse.Res>
}

const divisionData = [
  { name: "TOPIX", division: "0111" },
  { name: "東証マザーズ", division: "0114" },
  { name: "JQスタンダード", division: "0124" },
  { name: "JQグロース", division: "0126" },
]

const pieChartParamNames = [
  "NeageCount",
  "NesageCount",
  "KawarazuCount",
  "HikakuHukaCount",
]

const labels = ["値上がり率", "値下がり率", "変わらず", "比較不可"]

const percentageCalculation = (counts: Array<number>) => {
  const totalCount = reduce(counts, (total, x) => total + x, 0)
  return map(counts, (count: number) => {
    let num = Number((count / totalCount) * 100)
    num = Math.round(num)
    return isNaN(num) ? 0 : num
  })
}

export const StatisticList = (props: Props) => {
  const displayComponentList = [
    {
      title: "市場",
      cls: "market-cell",
      titleCls: "market-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        const divisionNum = get(data, "Division")
        const divisionName = get(
          find(divisionData, data => {
            return data.division === divisionNum
          }),
          "name"
        )
        return <>{divisionName}</>
      },
    },
    {
      title: "売買高 (万株)",
      cls: "regulation-cell",
      titleCls: "regulation-cell-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        return <>{Formatter.setNumber(data, "BaibaiDaka")}</>
      },
    },
    {
      title: "売買代金 (百万円)",
      cls: "currency-price-cell",
      titleCls: "currency-price-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        return <>{Formatter.setNumber(data, "BaibaiDaikin")}</>
      },
    },
    {
      title: (
        <div className="market-statistics-labels">
          {labels.map((name, i) => (
            <div className="market-statistics-label-item" key={i}>
              <span
                className="market-statistics-label-color"
                style={{ backgroundColor: chartColor[i] }}
              />
              {name}
            </div>
          ))}
        </div>
      ),
      cls: "piechart-labels-cell",
      titleCls: "piechart-labels-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        const pieChartData = map(pieChartParamNames, name => {
          return get(data, name)
        })
        return (
          <div className="market-statistics-piechart-col">
            <ReactResizeDetector
              handleHeight
              handleWidth
              render={({ width, height }) => (
                <PieChart
                  width={width}
                  height={height}
                  data={pieChartData}
                  labels={labels}
                  marketLength={props.statisticsData.length}
                />
              )}
            />
          </div>
        )
      },
    },
    {
      title: "値上がり率",
      cls: "changed-ratio-cell",
      titleCls: "changed-ratio-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        const pieChartData = map(pieChartParamNames, name => {
          return get(data, name)
        })
        const pieDataPercentages = percentageCalculation(pieChartData)
        return (
          <div className="liquid-row">
            <div className="change-price-value liquid-item">
              {Formatter.number(get(data, "NeageCount"))}
            </div>
            <div className="change-ratio-value liquid-item">
              <span className="change-price-value liquid-item">
                ({pieDataPercentages[0]}%)
              </span>
            </div>
          </div>
        )
      },
    },
    {
      title: "値下がり率",
      cls: "changed-ratio-cell",
      titleCls: "changed-ratio-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        const pieChartData = map(pieChartParamNames, name => {
          return get(data, name)
        })
        const pieDataPercentages = percentageCalculation(pieChartData)
        return (
          <div className="liquid-row">
            <div className="change-price-value liquid-item">
              {Formatter.number(get(data, "NesageCount"))}
            </div>
            <div className="change-ratio-value liquid-item">
              <span className="change-price-value liquid-item">
                ({pieDataPercentages[1]}%)
              </span>
            </div>
          </div>
        )
      },
    },
    {
      title: "変わらず",
      cls: "changed-ratio-cell",
      titleCls: "changed-ratio-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        const pieChartData = map(pieChartParamNames, name => {
          return get(data, name)
        })
        const pieDataPercentages = percentageCalculation(pieChartData)
        return (
          <div className="liquid-row">
            <div className="change-price-value liquid-item">
              {Formatter.number(get(data, "KawarazuCount"))}
            </div>
            <div className="change-ratio-value liquid-item">
              <span className="change-price-value liquid-item">
                ({pieDataPercentages[2]}%)
              </span>
            </div>
          </div>
        )
      },
    },
    {
      title: "比較不可",
      cls: "changed-ratio-cell",
      titleCls: "changed-ratio-title-cell title-cell",
      component: (data: APIResponse.Res) => {
        const pieChartData = map(pieChartParamNames, name => {
          return get(data, name)
        })
        const pieDataPercentages = percentageCalculation(pieChartData)
        return (
          <div className="liquid-row">
            <div className="change-price-value liquid-item">
              {Formatter.number(get(data, "HikakuHukaCount"))}
            </div>
            <div className="change-ratio-value liquid-item">
              <span className="change-price-value liquid-item">
                ({pieDataPercentages[3]}%)
              </span>
            </div>
          </div>
        )
      },
    },
  ]
  return (
    <table className="table table-striped market-statistics-table">
      <tbody>
        {displayComponentList.map((li, i) => {
          return (
            <tr key={i}>
              <td className={li.titleCls}>{li.title}</td>
              {props.statisticsData.map((data, j) => {
                return (
                  <td key={j} className={li.cls}>
                    {li.component(data)}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
